import {
  forceLink,
  forceManyBody,
  forceSimulation,
  forceX,
  forceY,
  type SimulationLinkDatum,
  type SimulationNodeDatum,
} from 'd3-force';

/**
 * **The whole graph's layout**: every record the org holds, placed at once for
 * the knowledge base's footer view.
 *
 * A different problem from the Network tab's (`network-graph.ts`), which lays
 * out thirty labelled nodes around one pinned subject. Here there are thousands
 * of unlabelled dots and no centre, so there is no collide-by-label-width and no
 * pin: link, charge and a gentle pull toward the middle, so that disconnected
 * clusters stay on one screen rather than drifting apart forever.
 *
 * Pure and deterministic — a seeded random source and a fixed tick budget — so
 * the same graph lands in the same shape twice, and so it can run in a Web
 * Worker (`whole-graph.worker.ts`) without the page freezing while it settles.
 */

/** Links by index into the node list, which is what crosses the worker boundary. */
export type IndexLink = readonly [number, number];

export interface WholeLayout {
  readonly x: Float64Array;
  readonly y: Float64Array;
}

export interface Bounds {
  readonly minX: number;
  readonly minY: number;
  readonly maxX: number;
  readonly maxY: number;
}

/** Ticks: enough for a few thousand nodes to settle, few enough to finish in seconds. */
export const WHOLE_GRAPH_TICKS = 300;

/**
 * Past this many records the picture is large: the view asks before drawing it,
 * and the layout spends fewer ticks (`layoutTicks`).
 */
export const LARGE_GRAPH = 3000;

/**
 * The tick budget for `count` records: the full budget up to `LARGE_GRAPH`, and
 * past it a budget that shrinks toward a floor, since each tick costs more. The
 * cooling is matched to the budget, so a short run still ends settled.
 *
 * The charge stays at d3's default Barnes–Hut theta. A coarser theta is faster
 * but leaves seams in the repulsion that unrelated records collect along, which
 * draws large false circles through the picture: the Punta Brava graph showed
 * them at theta 1.2 and 1.5 and not at 0.9 (2026-10-10).
 */
export function layoutTicks(count: number): number {
  if (count <= LARGE_GRAPH) return WHOLE_GRAPH_TICKS;
  return Math.max(120, Math.round((WHOLE_GRAPH_TICKS * LARGE_GRAPH) / count));
}

const LINK_DISTANCE = 24;
const CHARGE = -24;
const CHARGE_DISTANCE_MAX = 400;
const CENTER_PULL = 0.04;

type Node = SimulationNodeDatum & { index: number };

export function layoutWholeGraph(
  count: number,
  links: readonly IndexLink[],
  ticks = layoutTicks(count),
  onProgress?: (fraction: number) => void,
): WholeLayout {
  /* No initial positions: d3 seeds a phyllotaxis spiral, which is deterministic
     and already spread out, so the solve starts from something even. */
  const nodes: Node[] = Array.from({ length: count }, (_, index) => ({ index }));
  const simLinks: SimulationLinkDatum<Node>[] = links.map(([source, target]) => ({
    source,
    target,
  }));

  const simulation = forceSimulation<Node>(nodes)
    .randomSource(seededRandom(0x5eed))
    /* d3's default cools to its floor in 300 ticks; a shorter budget cools faster. */
    .alphaDecay(1 - Math.pow(0.001, 1 / Math.max(ticks, 1)))
    .force('link', forceLink<Node, SimulationLinkDatum<Node>>(simLinks).distance(LINK_DISTANCE))
    .force('charge', forceManyBody<Node>().strength(CHARGE).distanceMax(CHARGE_DISTANCE_MAX))
    .force('x', forceX<Node>(0).strength(CENTER_PULL))
    .force('y', forceY<Node>(0).strength(CENTER_PULL))
    .stop();

  const every = Math.max(1, Math.round(ticks / 50));
  for (let i = 0; i < ticks; i += 1) {
    simulation.tick();
    if (onProgress && (i + 1) % every === 0) onProgress((i + 1) / ticks);
  }

  const x = new Float64Array(count);
  const y = new Float64Array(count);
  for (const node of nodes) {
    x[node.index] = node.x ?? 0;
    y[node.index] = node.y ?? 0;
  }
  return { x, y };
}

/** The box every point sits in. An empty layout is a zero box at the origin. */
export function boundsOf(layout: WholeLayout): Bounds {
  if (layout.x.length === 0) return { minX: 0, minY: 0, maxX: 0, maxY: 0 };
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (let i = 0; i < layout.x.length; i += 1) {
    const px = layout.x[i]!;
    const py = layout.y[i]!;
    if (px < minX) minX = px;
    if (px > maxX) maxX = px;
    if (py < minY) minY = py;
    if (py > maxY) maxY = py;
  }
  return { minX, minY, maxX, maxY };
}

export interface View {
  /** Screen = layout × k + (x, y). */
  readonly x: number;
  readonly y: number;
  readonly k: number;
}

/** The zoom's range, for the fit and the reader's zoom alike. */
const MIN_K = 0.02;
const MAX_K = 40;

/**
 * The view that fits the whole box into a viewport, with a margin. A small
 * graph is spread to fill the panel too: the positions scale, while the marks
 * hold at their cap (`--mark-scale` in whole-graph.tsx), so three dots are
 * three dots far apart rather than three coins.
 */
export function fitView(bounds: Bounds, width: number, height: number, margin = 24): View {
  const w = Math.max(bounds.maxX - bounds.minX, 1);
  const h = Math.max(bounds.maxY - bounds.minY, 1);
  const k = clamp(Math.min((width - margin * 2) / w, (height - margin * 2) / h), MIN_K, MAX_K);
  return {
    k,
    x: width / 2 - ((bounds.minX + bounds.maxX) / 2) * k,
    y: height / 2 - ((bounds.minY + bounds.maxY) / 2) * k,
  };
}

/** A name the picture could print under its mark, in screen pixels. */
export interface LabelCandidate {
  readonly index: number;
  /** The mark's middle on screen. */
  readonly x: number;
  readonly y: number;
  /** The mark's radius on screen. */
  readonly r: number;
  readonly text: string;
  /** Lower places first: tracked people, then the most connected. */
  readonly rank: number;
}

export interface PlacedLabel {
  readonly index: number;
  /** The name's middle, and its box's top. */
  readonly x: number;
  readonly top: number;
  readonly text: string;
}

/** More marks than this on screen and the picture is dots only, as it was. */
export const LABEL_MARK_LIMIT = 120;
const LABEL_GAP = 3;
const LABEL_PAD = 2;

/**
 * Which names fit under their marks without touching another name, another
 * mark or the panel's edge, in rank order. A dense picture (more than
 * `LABEL_MARK_LIMIT` marks on screen) gets none: the whole graph is dots by
 * design, and names appear as a zoom spreads a cluster out.
 */
export function placeLabels(
  candidates: readonly LabelCandidate[],
  measure: (text: string) => number,
  width: number,
  height: number,
  lineHeight: number,
): PlacedLabel[] {
  const onScreen = candidates.filter((c) => c.x >= 0 && c.x <= width && c.y >= 0 && c.y <= height);
  if (onScreen.length > LABEL_MARK_LIMIT) return [];
  type Box = { l: number; t: number; r: number; b: number };
  const marks: (Box & { index: number })[] = onScreen.map((c) => ({
    index: c.index,
    l: c.x - c.r,
    t: c.y - c.r,
    r: c.x + c.r,
    b: c.y + c.r,
  }));
  const hits = (a: Box, b: Box) =>
    a.l < b.r + LABEL_PAD &&
    b.l < a.r + LABEL_PAD &&
    a.t < b.b + LABEL_PAD &&
    b.t < a.b + LABEL_PAD;
  const placed: (Box & { label: PlacedLabel })[] = [];
  for (const c of [...onScreen].sort((a, b) => a.rank - b.rank)) {
    const w = measure(c.text);
    const top = c.y + c.r + LABEL_GAP;
    const box = { l: c.x - w / 2, t: top, r: c.x + w / 2, b: top + lineHeight };
    if (box.l < 0 || box.r > width || box.b > height) continue;
    if (placed.some((p) => hits(p, box))) continue;
    if (marks.some((m) => m.index !== c.index && hits(m, box))) continue;
    placed.push({ ...box, label: { index: c.index, x: c.x, top, text: c.text } });
  }
  return placed.map((p) => p.label);
}

/**
 * The mark under a screen point: the nearest of `marks` (indices into the
 * layout) whose `reach` — its on-screen radius and any slop — covers the point,
 * or null. A canvas has no element per mark to hit, so this is the hit test.
 */
export function markAt(
  layout: WholeLayout,
  marks: readonly number[],
  view: View,
  sx: number,
  sy: number,
  reach: (at: number) => number,
): number | null {
  let best: number | null = null;
  let bestDistance = Infinity;
  for (const at of marks) {
    const distance = Math.hypot(
      layout.x[at]! * view.k + view.x - sx,
      layout.y[at]! * view.k + view.y - sy,
    );
    if (distance <= reach(at) && distance <= bestDistance) {
      best = at;
      bestDistance = distance;
    }
  }
  return best;
}

/** Zoom by `factor` about a screen point, which stays under the pointer. */
export function zoomAt(view: View, factor: number, sx: number, sy: number): View {
  const k = clamp(view.k * factor, MIN_K, MAX_K);
  const applied = k / view.k;
  return { k, x: sx - (sx - view.x) * applied, y: sy - (sy - view.y) * applied };
}

const clamp = (value: number, low: number, high: number): number =>
  Math.min(high, Math.max(low, value));

function seededRandom(seed: number): () => number {
  let state = seed >>> 0;
  return () => {
    state = (state * 1_664_525 + 1_013_904_223) >>> 0;
    return state / 4_294_967_296;
  };
}

/* ————————————————————————— the filter ————————————————————————— */

/** The chip for people nobody holds a record for. Statuses are their own words. */
export const NOT_TRACKED = 'not_tracked';
/** The chips for the two other kinds, one each. */
export const KIND_CHIP = { org: 'kind:org', event: 'kind:event' } as const;

interface FilterableNode {
  readonly id: string;
  readonly kind: string;
  readonly tracked: boolean;
  readonly status: string | null;
}

/**
 * The graph with every record whose chip is off taken out, and then every
 * record left tied to nothing on screen — a firm that was only on the picture
 * because of the people just hidden, or a person whose only tie was an event
 * just hidden, says nothing about what is left.
 *
 * Nothing off is the graph itself, the same object. With every chip on nothing
 * is untied — the server sends only records with a tie — so the rule can only
 * bite once something is hidden. A tracked person with no status, or one this
 * build has no word for, has no chip and is hidden only by being left untied.
 */
export function filterGraph<
  G extends {
    readonly nodes: readonly FilterableNode[];
    readonly edges: readonly { a: string; b: string }[];
  },
>(graph: G, off: ReadonlySet<string>): G {
  if (off.size === 0) return graph;
  const hidden = (node: FilterableNode): boolean => {
    if (node.kind === 'org') return off.has(KIND_CHIP.org);
    if (node.kind === 'event') return off.has(KIND_CHIP.event);
    return node.tracked ? node.status !== null && off.has(node.status) : off.has(NOT_TRACKED);
  };

  const kept = new Set(graph.nodes.filter((node) => !hidden(node)).map((node) => node.id));
  const edges = graph.edges.filter((edge) => kept.has(edge.a) && kept.has(edge.b));
  const tied = new Set<string>();
  for (const edge of edges) {
    tied.add(edge.a);
    tied.add(edge.b);
  }
  const nodes = graph.nodes.filter((node) => tied.has(node.id));
  return { ...graph, nodes, edges };
}
